/**
 * Status chips, in one file so a status never reads one way on the orders
 * screen and another on the payments screen.
 *
 * The enum values are the API's contract and never change; these labels are
 * operator-facing and can be reworded freely — which is exactly why they live
 * here rather than inline at four call sites.
 */
import type {
  AdminProduct,
  MessageStatus,
  OrderStatus,
  PaymentStatus,
  ProductStatus,
  RefundStatus,
  ShipmentStatus,
  StockReason,
} from "@/lib/api/types";
import { cn } from "@/lib/utils";

type Tone = "neutral" | "info" | "good" | "warn" | "bad";

const TONE: Record<Tone, string> = {
  neutral: "bg-canvas text-muted",
  info: "bg-info-tint text-info",
  good: "bg-good-tint text-good",
  warn: "bg-warn-tint text-warn",
  bad: "bg-bad-tint text-bad",
};

function Chip({ label, tone }: { label: string; tone: Tone }) {
  return (
    <span
      className={cn(
        "inline-block rounded-full px-2 py-0.5 text-[10px] font-bold tracking-[0.04em] whitespace-nowrap uppercase",
        TONE[tone],
      )}
    >
      {label}
    </span>
  );
}

const ORDER: Record<OrderStatus, { label: string; tone: Tone }> = {
  // Amber rather than grey: an online order sitting in PENDING is holding stock
  // for something nobody has paid for, which is a state worth noticing in a list.
  PENDING: { label: "Pending", tone: "warn" },
  CONFIRMED: { label: "Confirmed", tone: "info" },
  PACKED: { label: "Packed", tone: "info" },
  SHIPPED: { label: "Shipped", tone: "info" },
  DELIVERED: { label: "Delivered", tone: "good" },
  CANCELLED: { label: "Cancelled", tone: "bad" },
};

const PAYMENT: Record<PaymentStatus, { label: string; tone: Tone }> = {
  CREATED: { label: "Not attempted", tone: "neutral" },
  PENDING: { label: "Pending", tone: "warn" },
  PAID: { label: "Paid", tone: "good" },
  FAILED: { label: "Failed", tone: "bad" },
  // Not an error and not shown as one: the customer closed the window. It is a
  // cart to win back, which is a different thing from a declined card.
  USER_DROPPED: { label: "Abandoned", tone: "neutral" },
  EXPIRED: { label: "Expired", tone: "neutral" },
  REFUNDED: { label: "Refunded", tone: "info" },
  PARTIALLY_REFUNDED: { label: "Part refunded", tone: "info" },
};

const REFUND: Record<RefundStatus, { label: string; tone: Tone }> = {
  PENDING: { label: "In progress", tone: "warn" },
  ONHOLD: { label: "On hold", tone: "warn" },
  SUCCESS: { label: "Refunded", tone: "good" },
  FAILED: { label: "Failed", tone: "bad" },
  CANCELLED: { label: "Cancelled", tone: "neutral" },
};

/**
 * Courier status.
 *
 * Two tones carry the operational weight. UNDELIVERED is red rather than amber
 * because an NDR is a parcel that comes back at our cost if nobody acts, and it
 * arrives only once a day — there is no second chance to notice it. RTO is red
 * for the same reason, one stage later: it is already on its way back.
 *
 * DRAFT is amber, not grey: a row that never reached the courier is a booking
 * an operator believes they made. Nothing will send it on its own.
 */
const SHIPMENT: Record<ShipmentStatus, { label: string; tone: Tone }> = {
  DRAFT: { label: "Not sent", tone: "warn" },
  BOOKED: { label: "Booked", tone: "info" },
  PICKED_UP: { label: "Picked up", tone: "info" },
  IN_TRANSIT: { label: "In transit", tone: "info" },
  OUT_FOR_DELIVERY: { label: "Out for delivery", tone: "info" },
  DELIVERED: { label: "Delivered", tone: "good" },
  UNDELIVERED: { label: "NDR", tone: "bad" },
  RTO: { label: "Returning", tone: "bad" },
  RETURNED: { label: "Returned", tone: "neutral" },
  CANCELLED: { label: "Cancelled", tone: "neutral" },
  FAILED: { label: "Booking failed", tone: "bad" },
};

export const OrderChip = ({ status }: { status: OrderStatus }) => <Chip {...ORDER[status]} />;
export const ShipmentChip = ({ status }: { status: ShipmentStatus }) => (
  <Chip {...SHIPMENT[status]} />
);

export function shipmentLabel(status: ShipmentStatus): string {
  return SHIPMENT[status].label;
}
export const PaymentChip = ({ status }: { status: PaymentStatus }) => <Chip {...PAYMENT[status]} />;
export const RefundChip = ({ status }: { status: RefundStatus }) => <Chip {...REFUND[status]} />;

export function orderLabel(status: OrderStatus): string {
  return ORDER[status].label;
}

const PRODUCT: Record<ProductStatus, { label: string; tone: Tone }> = {
  ACTIVE: { label: "Live", tone: "good" },
  // Not an error state — a product waiting on artwork or a launch date. Grey,
  // so it does not compete for attention with a shelf that is actually empty.
  DRAFT: { label: "Draft", tone: "neutral" },
  ARCHIVED: { label: "Archived", tone: "neutral" },
};

const REASON: Record<StockReason, { label: string; tone: Tone }> = {
  SALE: { label: "Sale", tone: "neutral" },
  CANCELLATION: { label: "Cancelled", tone: "info" },
  RESTOCK: { label: "Restock", tone: "good" },
  // The one worth spotting in a scroll: a correction means the system and the
  // shelf disagreed, and a run of them is a process problem, not a data entry one.
  CORRECTION: { label: "Correction", tone: "warn" },
  SEED: { label: "Migration", tone: "neutral" },
};

export const ProductChip = ({ status }: { status: ProductStatus }) => <Chip {...PRODUCT[status]} />;
export const ReasonChip = ({ reason }: { reason: StockReason }) => <Chip {...REASON[reason]} />;

/**
 * The stock count, said the way an operator reads it.
 *
 * The number is always shown — that is the entire reason the admin surface
 * carries `stockQuantity` at all — and the colour is the fast scan on top of
 * it. Zero is red because it is lost sales right now; at-or-below-threshold is
 * amber because it is a purchase order, not an emergency.
 */
export function StockCount({
  product,
}: {
  product: Pick<AdminProduct, "stockQuantity" | "lowStockThreshold" | "outOfStock" | "lowStock">;
}) {
  const tone = product.outOfStock ? "text-bad" : product.lowStock ? "text-warn" : "text-ink";

  return (
    <span className="whitespace-nowrap">
      <span className={cn("font-semibold tabular-nums", tone)}>{product.stockQuantity}</span>
      <span className="ml-1.5 text-[11px] text-faint tabular-nums">
        / {product.lowStockThreshold}
      </span>
    </span>
  );
}

/**
 * Message delivery, and the one label here that is a deliberate understatement.
 *
 * SENT is shown as "Sent", never "Delivered", because that is all it means: the
 * provider accepted the handoff. Only a provider webhook can establish that a
 * human received anything (ADMIN.md §7.5), and calling it delivered would put
 * a claim on the screen that support would then repeat to a customer.
 *
 * SKIPPED is grey and reads "Not configured" rather than anything failure-shaped.
 * It is the state of every message on a machine with no ZeptoMail token, and
 * colouring it red would train an operator to ignore red.
 */
const MESSAGE: Record<MessageStatus, { label: string; tone: Tone }> = {
  // Amber, not grey: a row still QUEUED is a send that died between the log
  // write and the provider call. Nothing will retry it on its own.
  QUEUED: { label: "Queued", tone: "warn" },
  SENT: { label: "Sent", tone: "good" },
  DELIVERED: { label: "Delivered", tone: "good" },
  FAILED: { label: "Failed", tone: "bad" },
  BOUNCED: { label: "Bounced", tone: "bad" },
  SKIPPED: { label: "Not configured", tone: "neutral" },
};

export const MessageChip = ({ status }: { status: MessageStatus }) => <Chip {...MESSAGE[status]} />;
